import { BaseService } from "./baseService"
import { ChatToken, ChatTokenRequest, ChatTokenResponse, StreamChatTokenRequest } from "./types"

export class ChatService extends BaseService {

  /**
   * Delete a chat message
   */
  static async deleteMessage(
    chatArn: string,
    messageId: string
  ): Promise<void> {
    try {
      const response = await this.post("/chat/delete-message", {
        chatArn,
        messageId,
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      console.error("Error deleting chat message:", error);
      // Don't throw error to avoid disrupting chat flow
    }
  }

  /**
   * Get a simplified chat token for StreamChat component
   */
  static async getStreamChatToken(
    request: StreamChatTokenRequest
  ): Promise<ChatToken> {
    try {
      const response = await this.post("/chat/token", request);
      const data = await this.handleResponse<ChatTokenResponse>(response);

      return {
        token: data.token,
        sessionExpirationTime: new Date(data.sessionExpirationTime),
        tokenExpirationTime: new Date(data.tokenExpirationTime),
      };
    } catch (error) {
      console.error("Error getting stream chat token:", error);
      throw new Error(`Failed to get chat token: ${error}`);
    }
  }

  /**
   * Create a token provider function for AWS IVS Chat SDK
   */
  static createTokenProvider(chatArn: string, isModerator: boolean, userDisplayName?: string, avatarUrl?: string): () => Promise<ChatToken> {
    // Validate chatArn format
    console.log('Creating token provider for chatArn:',  chatArn, 'isModerator:', isModerator, 'userDisplayName:', userDisplayName, 'avatarUrl:', avatarUrl);
    const arnPattern = /^arn:aws:ivschat:[a-z0-9-]+:[0-9]+:room\/[a-zA-Z0-9-]+$/;
    if (!chatArn || !arnPattern.test(chatArn)) {
      console.error('Invalid chatArn format:', chatArn);
      console.error('Expected format: arn:aws:ivschat:region:account:room/room-name');
      // For now, let's try to use the chatArn as-is and see what the API returns
      console.warn('Attempting to use chatArn as-is despite invalid format:', chatArn);
    }

    return () => this.getStreamChatToken({
      chatRoomArn: chatArn,
      isHost: isModerator,
      attributes: {
        username: userDisplayName || 'Anonymous',
        ...(avatarUrl && { avatar: avatarUrl })
      },
    });
  }

  /**
   * Store a chat message in the backend database
   */
  static async storeMessage(
    chatArn: string,
    messageId: string,
    content: string,
    sender: string,
    userId?: string,
    avatar?: string,
    timestamp?: Date
  ): Promise<void> {
    try {
      const response = await this.post("/chat/store-message", {
        chatArn,
        messageId,
        content,
        sender,
        userId,
        avatar,
        timestamp: timestamp?.toISOString() || new Date().toISOString(),
      });
      await this.handleResponse<void>(response);
    } catch (error) {
      console.error("Error storing chat message:", error);
      // Don't throw error to avoid disrupting chat flow
    }
  }

  /**
   * Get historical messages for a chat room
   */
  static async getHistoricalMessages(
    chatArn: string,
    limit: number = 50
  ): Promise<any[]> {
    try {
      const response = await this.get(`/chat/messages?chatArn=${encodeURIComponent(chatArn)}&limit=${limit}`);
      return await this.handleResponse<any[]>(response);
    } catch (error) {
      console.error("Error getting historical messages:", error);
      // Return empty array on error to avoid breaking chat initialization
      return [];
    }
  }
}